// src/pages/merchant/Dashboard.js
// Dashboard marchand : statistiques
// Maxula

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiPackage, FiShoppingBag, FiClock, FiCheckCircle, FiEye, FiDollarSign, FiPlus, FiCreditCard, FiExternalLink, FiAlertCircle } from 'react-icons/fi';
import { merchantAPI, subscriptionAPI } from '../../services/api';
import MerchantCharts from '../../components/merchant/MerchantCharts';
import GlowButton from '../../components/ui/GlowButton';

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
    <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: `${color}1a`, color }}>
      <Icon size={22} />
    </div>
    <div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  </div>
);

const MerchantDashboard = () => {
  const [stats, setStats] = useState(null);
  const [merchant, setMerchant] = useState(null);
  const [recent, setRecent] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await merchantAPI.getCurrentDashboard();
        const d = res.data?.data || {};
        setStats(d.stats || {});
        setMerchant(d.merchant || {});
        setRecent(d.recentDeals || []);
        try {
          const s = await subscriptionAPI.getMine();
          setSub(s.data?.data?.limits || null);
        } catch (e) {}
      } catch (e) {
        // silencieux
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" /></div>;
  }

  const s = stats || {};
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tableau de bord</h1>
          <p className="text-gray-500">{merchant?.businessName}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(merchant?.slug || merchant?.id || merchant?._id) && (
            <GlowButton href={`/merchants/${merchant.slug || merchant.id || merchant._id}`} icon={FiExternalLink} title="Voir ma boutique publique" variant="green">
              Voir ma boutique
            </GlowButton>
          )}
          <Link to="/merchant/deals/new" className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            <FiPlus /> Nouveau deal
          </Link>
        </div>
      </div>

      {/* Compte pas encore valide par un admin : sans ce bandeau, le marchand
          cree des annonces qui restent bloquees en attente sans comprendre
          pourquoi, et ouvre un ticket au support. */}
      {merchant && (merchant.status === 'pending' || merchant.verified === false) && (
        <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
          <FiAlertCircle className="text-amber-600 mt-0.5 flex-shrink-0" size={20} />
          <div className="text-sm text-amber-900">
            <strong>Votre compte est en attente de validation.</strong>
            <div className="mt-1">
              Vous pouvez préparer et enregistrer vos annonces dès maintenant, mais
              elles ne seront publiées qu'après la validation de votre compte par
              notre équipe. Vous recevrez une notification et un e-mail dès que
              c'est fait.
            </div>
          </div>
        </div>
      )}

      {sub && (
        <div className="mb-6 rounded-xl border border-primary-200 bg-primary-50 p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-gray-700">
            Pack actuel : <strong>{sub.name}</strong> — {sub.maxDeals === -1 ? 'deals illimités' : `${sub.maxDeals} deals`}, commission {sub.commissionRate}%
          </div>
          <Link to="/merchant/subscription" className="text-sm font-semibold text-primary-700 inline-flex items-center gap-1 hover:underline">
            <FiCreditCard size={14} /> Gérer l'abonnement
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <Stat icon={FiPackage} label="Deals actifs" value={s.activeDeals || 0} color="#2563eb" />
        <Stat icon={FiPackage} label="Total deals" value={s.totalDeals || 0} color="#7c3aed" />
        <Stat icon={FiShoppingBag} label="Commandes" value={s.totalOrders || 0} color="#0891b2" />
        <Stat icon={FiClock} label="En attente" value={s.pendingOrders || 0} color="#d97706" />
        <Stat icon={FiCheckCircle} label="Complétées" value={s.completedOrders || 0} color="#16a34a" />
        <Stat icon={FiDollarSign} label="Revenu (MAD)" value={(s.revenue || 0).toLocaleString('fr-FR')} color="#db2777" />
      </div>

      <MerchantCharts />

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-900">Deals récents</h2>
          <Link to="/merchant/deals" className="text-sm text-primary-600 hover:underline">Voir tout</Link>
        </div>
        {recent.length === 0 ? (
          <div className="text-center text-gray-400 py-8">Aucun deal pour le moment.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {recent.map((d) => (
              <div key={d._id} className="py-3 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 truncate">{d.title}</div>
                  <div className="text-xs text-gray-400 flex items-center gap-3">
                    <span className="inline-flex items-center gap-1"><FiEye size={12} /> {d.views || 0}</span>
                    <span>{d.quantitySold || 0} vendus</span>
                    <span className="capitalize">{d.status}</span>
                  </div>
                </div>
                <div className="font-semibold text-gray-900">{d.discountedPrice || d.price || 0} MAD</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MerchantDashboard;
